import { Controller } from "@hotwired/stimulus"

// Renders a Mermaid workflow diagram with pan/zoom controls and label toggling.
//
// Targets:
//   container       - the overflow-hidden viewport
//   diagram         - the inner element that gets transformed
//   labelToggle     - the toggle button (internal users only)
//   labelToggleText - the text span inside the toggle button
//
// Values:
//   plain     - Mermaid source with plain-language labels
//   technical - Mermaid source with technical labels
//   tooltips  - JSON map of node ID -> description
export default class extends Controller {
  static targets = ["container", "diagram", "labelToggle", "labelToggleText"]
  static values = {
    plain: String,
    technical: String,
    tooltips: String
  }

  connect() {
    this.scale = 1
    this.panX = 0
    this.panY = 0
    this.isPanning = false
    this.startX = 0
    this.startY = 0
    this.labelMode = "plain"
    this.minScale = 0.3
    this.maxScale = 3
    this.panZoomInitialized = false
    this.isActive = false

    this.loadAndRender()
  }

  disconnect() {
    this.removePanListeners()
    this.removeActivationListeners()
  }

  async loadAndRender() {
    try {
      await this.loadMermaid()
      await this.renderDiagram()
      this.setupPanZoom()
      this.attachTooltips()
    } catch (error) {
      console.error("Failed to render workflow diagram:", error)
      this.showError()
    }
  }

  async loadMermaid() {
    if (window.mermaid) return

    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = "https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"
      script.onload = () => {
        window.mermaid.initialize({
          startOnLoad: false,
          theme: "base",
          themeVariables: {
            primaryColor: "#7C3AED",
            primaryTextColor: "#fff",
            primaryBorderColor: "#5B21B6",
            lineColor: "#6B7280",
            secondaryColor: "#F3F4F6",
            tertiaryColor: "#fff",
            fontFamily: "Neue Haas Grotesk Text Pro, system-ui, sans-serif",
            fontSize: "12px"
          },
          flowchart: {
            htmlLabels: true,
            curve: "basis",
            padding: 8,
            nodeSpacing: 30,
            rankSpacing: 40
          }
        })
        resolve()
      }
      script.onerror = () => reject(new Error("Failed to load Mermaid"))
      document.head.appendChild(script)
    })
  }

  async renderDiagram() {
    const source = this.labelMode === "technical" ? this.technicalValue : this.plainValue

    if (!source) {
      this.showError("No diagram data provided")
      return
    }

    const id = `workflow-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

    try {
      const { svg } = await window.mermaid.render(id, source)
      this.diagramTarget.innerHTML = svg

      const svgEl = this.diagramTarget.querySelector("svg")
      if (svgEl) {
        svgEl.style.height = "auto"
        svgEl.style.minWidth = "fit-content"
      }
    } catch (error) {
      console.error("Mermaid render error:", error)
      this.showError("Failed to render diagram")
    }
  }

  // ===== Pan/Zoom =====

  setupPanZoom() {
    if (this.panZoomInitialized) return
    this.panZoomInitialized = true

    const container = this.containerTarget

    this.boundPointerDown = this.onPointerDown.bind(this)
    this.boundPointerMove = this.onPointerMove.bind(this)
    this.boundPointerUp = this.onPointerUp.bind(this)
    this.boundWheel = this.onWheel.bind(this)

    container.addEventListener("pointerdown", this.boundPointerDown)
    container.addEventListener("pointermove", this.boundPointerMove)
    container.addEventListener("pointerup", this.boundPointerUp)
    container.addEventListener("pointerleave", this.boundPointerUp)
    container.addEventListener("pointercancel", this.boundPointerUp)
    container.addEventListener("lostpointercapture", this.boundPointerUp)
    container.addEventListener("wheel", this.boundWheel, { passive: false })

    this.setupActivationListeners()
    this.fitToView()
  }

  removePanListeners() {
    if (!this.panZoomInitialized) return
    this.panZoomInitialized = false

    const container = this.containerTarget
    if (!container) return

    container.removeEventListener("pointerdown", this.boundPointerDown)
    container.removeEventListener("pointermove", this.boundPointerMove)
    container.removeEventListener("pointerup", this.boundPointerUp)
    container.removeEventListener("pointerleave", this.boundPointerUp)
    container.removeEventListener("pointercancel", this.boundPointerUp)
    container.removeEventListener("lostpointercapture", this.boundPointerUp)
    container.removeEventListener("wheel", this.boundWheel)
  }

  // ===== Activation =====
  //
  // The diagram only captures wheel/touch gestures after the user clicks into
  // it. Otherwise the page scrolls normally over the diagram, so a wheel-mouse
  // doesn't get hijacked into zooming.

  setupActivationListeners() {
    this.boundActivate = this.activate.bind(this)
    this.boundDocumentPointerDown = this.onDocumentPointerDown.bind(this)

    this.element.addEventListener("pointerdown", this.boundActivate, true)
    document.addEventListener("pointerdown", this.boundDocumentPointerDown, true)
  }

  removeActivationListeners() {
    if (this.boundActivate) {
      this.element.removeEventListener("pointerdown", this.boundActivate, true)
    }
    if (this.boundDocumentPointerDown) {
      document.removeEventListener("pointerdown", this.boundDocumentPointerDown, true)
    }
  }

  activate() {
    if (this.isActive) return
    this.isActive = true
    this.containerTarget.classList.add("ring-2", "ring-core-purple-500")
    this.containerTarget.style.touchAction = "none"
  }

  deactivate() {
    if (!this.isActive) return
    this.isActive = false
    this.isPanning = false
    this.containerTarget.classList.remove("ring-2", "ring-core-purple-500")
    this.containerTarget.style.touchAction = "auto"
  }

  onDocumentPointerDown(e) {
    if (!this.element.contains(e.target)) {
      this.deactivate()
    }
  }

  onPointerDown(e) {
    if (e.button !== 0) return
    if (!this.isActive) return
    this.isPanning = true
    this.startX = e.clientX - this.panX
    this.startY = e.clientY - this.panY
    this.containerTarget.setPointerCapture(e.pointerId)
  }

  onPointerMove(e) {
    if (!this.isPanning) return
    this.panX = e.clientX - this.startX
    this.panY = e.clientY - this.startY
    this.applyTransform()
  }

  onPointerUp() {
    this.isPanning = false
  }

  onWheel(e) {
    if (!this.isActive) return
    e.preventDefault()

    const rect = this.containerTarget.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    const delta = e.deltaY > 0 ? 0.9 : 1.1
    const newScale = Math.min(this.maxScale, Math.max(this.minScale, this.scale * delta))

    const ratio = newScale / this.scale
    this.panX = mouseX - ratio * (mouseX - this.panX)
    this.panY = mouseY - ratio * (mouseY - this.panY)
    this.scale = newScale

    this.applyTransform()
  }

  applyTransform() {
    this.diagramTarget.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`
  }

  // ===== Zoom Controls =====

  zoomIn() {
    this.scale = Math.min(this.maxScale, this.scale * 1.25)
    this.applyTransform()
  }

  zoomOut() {
    this.scale = Math.max(this.minScale, this.scale * 0.8)
    this.applyTransform()
  }

  resetZoom() {
    this.fitToView()
  }

  fitToView() {
    const svgEl = this.diagramTarget.querySelector("svg")
    if (!svgEl) return

    const containerRect = this.containerTarget.getBoundingClientRect()

    let svgWidth, svgHeight
    try {
      const bbox = svgEl.getBBox()
      svgWidth = bbox.width || svgEl.scrollWidth || 800
      svgHeight = bbox.height || svgEl.scrollHeight || 400
    } catch {
      svgWidth = svgEl.scrollWidth || 800
      svgHeight = svgEl.scrollHeight || 400
    }

    const scaleX = containerRect.width / svgWidth
    const scaleY = containerRect.height / svgHeight
    this.scale = Math.min(scaleX, scaleY, 1) * 0.95

    this.panX = (containerRect.width - svgWidth * this.scale) / 2
    this.panY = (containerRect.height - svgHeight * this.scale) / 2

    this.applyTransform()
  }

  // ===== Label Toggle =====

  async toggleLabels() {
    this.labelMode = this.labelMode === "plain" ? "technical" : "plain"

    if (this.hasLabelToggleTextTarget) {
      this.labelToggleTextTarget.textContent = this.labelMode === "plain" ? "Show Technical" : "Show Plain"
    }

    await this.renderDiagram()
    this.attachTooltips()
    this.fitToView()
  }

  // ===== Tooltips =====

  attachTooltips() {
    let tooltips = {}
    try {
      tooltips = JSON.parse(this.tooltipsValue || "{}")
    } catch {
      return
    }

    const svgEl = this.diagramTarget.querySelector("svg")
    if (!svgEl) return

    const nodeIndex = new Map()
    svgEl.querySelectorAll(".node").forEach(node => {
      const id = node.getAttribute("id") || ""
      nodeIndex.set(id, node)
    })

    for (const [nodeId, description] of Object.entries(tooltips)) {
      const prefix = `flowchart-${nodeId}-`
      for (const [id, node] of nodeIndex) {
        if (id === prefix || id.startsWith(prefix)) {
          node.setAttribute("title", description)
          node.style.cursor = "help"

          let titleEl = node.querySelector("title")
          if (!titleEl) {
            titleEl = document.createElementNS("http://www.w3.org/2000/svg", "title")
            node.prepend(titleEl)
          }
          titleEl.textContent = description
        }
      }
    }
  }

  // ===== Error Handling =====

  showError(message = "Failed to load workflow diagram") {
    const wrapper = document.createElement("div")
    wrapper.className = "text-center py-8 text-support-dark-500"

    const msg = document.createElement("div")
    msg.className = "mb-2"
    msg.textContent = message
    wrapper.appendChild(msg)

    const btn = document.createElement("button")
    btn.className = "text-sm text-core-purple-600 hover:underline"
    btn.setAttribute("data-action", "workflow-diagram#retry")
    btn.textContent = "Try again"
    wrapper.appendChild(btn)

    this.diagramTarget.replaceChildren(wrapper)
  }

  retry() {
    this.diagramTarget.innerHTML = `
      <div class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-core-purple-600"></div>
      </div>
    `
    this.loadAndRender()
  }
}
